import { IPhoneNumber } from '../../../../models';

/**
 * Type that represents a recursive non-nullable version of T
 */
export type NonNullableRecursive<T> = T extends null
  ? never
  : T extends Array<infer U>
  ? Array<NonNullableRecursive<NonNullable<U>>>
  : T extends object
  ? {
      [K in keyof T as T[K] extends null ? never : K]: NonNullableRecursive<
        NonNullable<T[K]>
      >;
    }
  : T;

/**
 * Recursively removes all null values from an object
 * @param obj The object to remove null values from
 * @returns A new object with all null values removed
 */
export const removeNulls = <T>(obj: T): NonNullableRecursive<T> => {
  // Handle non-objects
  if (obj === null || typeof obj !== 'object') {
    return obj as NonNullableRecursive<T>;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj
      .filter((item) => item !== null)
      .map((item) => removeNulls(item)) as NonNullableRecursive<T>;
  }

  // Handle objects
  const result: Record<string, any> = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      if (value !== null) {
        result[key] = removeNulls(value);
      }
    }
  }

  return result as NonNullableRecursive<T>;
};

/**
 * Clear empty phone numbers from the array of phone numbers
 *
 * @Example
 * ```ts
 * const phoneNumbers = [{ type: PhoneNumberType.OTHER, phoneNumber: '1234567890' }, { type: PhoneNumberType.OTHER, phoneNumber: '' }];
 *
 * const clearedPhoneNumbers = clearEmptyPhoneNumbers(phoneNumbers);
 *
 * console.log(clearedPhoneNumbers);
 * // [{ type: PhoneNumberType.OTHER, phoneNumber: '1234567890' }]
 * ```
 *
 * @param phoneNumbers - The array of phone numbers to clear
 * @returns The array of phone numbers with empty phone numbers removed
 */
export const clearEmptyPhoneNumbers = (phoneNumbers: IPhoneNumber[]) => {
  return phoneNumbers.filter((phoneNumber) => phoneNumber.phoneNumber !== '');
};
